/**
 * Consolidated IP utilities for SMFA applications
 * Provides reliable IP address detection with fallbacks and caching
 */

import { get, isFunction } from "lodash";
import { setIpAddress } from "./store";

// Cache configuration
const IP_CACHE_KEY = "cached_ip_info";
const IP_CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours
let activeIpRequest = null;

/**
 * Get IP information from public APIs with fallbacks and caching
 * @param {boolean} bypassCache - Whether to bypass the cache
 * @returns {Promise<Object>} IP information object
 */
export const getIpInfo = async (bypassCache = false) => {
  try {
    // Return existing promise if already in progress
    if (activeIpRequest) {
      return activeIpRequest;
    }

    // Check cache unless bypassing
    if (!bypassCache) {
      const cachedInfo = getCachedIpInfo();
      if (cachedInfo) {
        return cachedInfo;
      }
    }

    // Create a promise for the request
    activeIpRequest = (async () => {
      try {
        // Try multiple services with fetch API
        const services = [
          { name: "ipapi.co", url: "https://ipapi.co/json/" },
          { name: "ipify.org", url: "https://api.ipify.org?format=json" },
          {
            name: "api64.ipify.org",
            url: "https://api64.ipify.org?format=json",
          },
          { name: "jsonip.com", url: "https://jsonip.com" },
        ];

        for (const service of services) {
          try {
            const response = await fetch(service.url, {
              method: "GET",
              mode: "cors",
              credentials: "omit", // Never send credentials
              cache: "no-cache",
              headers: { Accept: "application/json" },
            });

            if (!response.ok) continue;

            const data = await response.json();
            if (!data || !data.ip) continue;

            // Format response based on the service
            let ipInfo;
            if (service.name === "ipapi.co") {
              ipInfo = {
                ip: data.ip,
                city: data.city,
                region: data.region,
                country: data.country,
                loc: `${data.latitude},${data.longitude}`,
                org: data.org || null,
                postal: data.postal || null,
                timezone: data.timezone || null,
                source: service.name,
              };
            } else {
              ipInfo = {
                ip: data.ip,
                city: null,
                region: null,
                country: null,
                loc: null,
                source: service.name,
              };
            }

            // Cache the successful result
            cacheIpInfo(ipInfo);
            return ipInfo;
          } catch (error) {
            // Continue to next service
          }
        }

        // If all services fail, try backend endpoint
        try {
          const backendUrl = (
            process.env.REACT_APP_BACKEND_URL || "http://localhost:8069"
          ).replace(/\/$/, "");
          const response = await fetch(`${backendUrl}/api/public/ip`, {
            method: "GET",
            mode: "cors",
            credentials: "omit",
            cache: "no-cache",
          });

          if (response.ok) {
            const data = await response.json();
            if (data.status && data.data?.ip) {
              const ipInfo = {
                ip: data.data.ip,
                city: null,
                region: null,
                country: null,
                loc: null,
                source: "backend",
              };

              cacheIpInfo(ipInfo);
              return ipInfo;
            }
          }
        } catch (error) {
          // Continue to fallback
        }

        // Return fallback object if all methods fail
        return {
          ip: "0.0.0.0",
          city: null,
          region: null,
          country: null,
          loc: null,
          fallback: true,
          source: "fallback",
        };
      } finally {
        setTimeout(() => {
          activeIpRequest = null;
        }, 1000);
      }
    })();

    return activeIpRequest;
  } catch (error) {
    activeIpRequest = null;
    return {
      ip: "0.0.0.0",
      fallback: true,
      source: "error",
    };
  }
};

/**
 * Get client's IP address with fallbacks
 * @param {boolean} bypassCache - Whether to bypass the cache
 * @returns {Promise<string|null>} IP address or null
 */
export const getIpAddress = async (dispatch = null, options = {}) => {
  const { bypassCache = false } = options;
  let ip = "0.0.0.0";
  // Check cache unless bypassing
  if (!bypassCache) {
    const cachedInfo = getCachedIpInfo();
    ip = get(cachedInfo, "ip") ? cachedInfo.ip : ip;
  }

  const ipInfo = await getIpInfo(true);
  ip = get(ipInfo, "ip") ? ipInfo.ip : ip;

  if (isFunction(dispatch)) {
    dispatch(setIpAddress(ip));
  }

  return ip;
};

/**
 * Cache IP information to local storage
 * @param {Object} ipInfo - The IP information to cache
 */
export const cacheIpInfo = (ipInfo) => {
  if (!ipInfo || !ipInfo.ip || ipInfo.ip === "0.0.0.0") {
    return;
  }

  try {
    const cacheData = {
      data: ipInfo,
      timestamp: Date.now(),
    };
    localStorage.setItem(IP_CACHE_KEY, JSON.stringify(cacheData));
  } catch (e) {
    // Ignore cache errors
  }
};

/**
 * Get cached IP information if valid
 * @returns {Object|null} Cached IP information or null
 */
export const getCachedIpInfo = () => {
  try {
    const cached = localStorage.getItem(IP_CACHE_KEY);
    if (!cached) {
      return null;
    }

    const cacheData = JSON.parse(cached);
    const now = Date.now();

    if (
      cacheData &&
      cacheData.timestamp &&
      now - cacheData.timestamp < IP_CACHE_DURATION
    ) {
      return cacheData.data;
    }

    // Cache expired
    return null;
  } catch (e) {
    return null;
  }
};

/**
 * Clear all IP caches
 */
export const clearIpCaches = () => {
  const cacheKeys = ["cached_ip_info", "ip_cache"];

  cacheKeys.forEach((key) => {
    try {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    } catch (e) {
      // Ignore errors
    }
  });

  return true;
};

// Export for browser console debugging
if (typeof window !== "undefined") {
  window.ipUtils = {
    getIpAddress,
    clearIpCaches,
    getCachedIpInfo,
  };
}
